import React, { useEffect, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { StyleSettings } from "../../styles/StyleSettings";
import { onSearchStore } from "@shared/libs/Global";
import { SnLabel } from "./SnLabel";

interface SnYearPeriodBoxProps {
  defaultValue?: string;
  width?: number;
  onValueChanged?: (value: string, end: string) => void;
  isReadOnly?: boolean;
  isRequired?: boolean;
  isEditable?: boolean;
  inputRef?: any;
  isSearchPanel?: boolean;
  onFocus?: (target: string) => void;
}

const SnYearPeriodBox = React.memo(
  ({
    defaultValue,
    onValueChanged,
    isReadOnly = false,
    isRequired,
    isEditable = true,
    inputRef,
    isSearchPanel = false,
    onFocus,
  }: SnYearPeriodBoxProps) => {
    const newDate = dayjs(new Date());
    const aftetDate = dayjs(new Date()).add(1, "year");
    const start: string = defaultValue ? defaultValue.split(",")[0].trim() : "";
    const end: string = defaultValue ? defaultValue.split(",")[1].trim() : "";
    const [value, setValue] = useState<[Dayjs, Dayjs]>([
      dayjs(start || newDate),
      dayjs(end || aftetDate),
    ]);
    const [isPopperOpen, setIsPopperOpen] = useState(false);
    const divRef = useRef<HTMLDivElement | null>(null);
    const secondRef = useRef<HTMLDivElement | null>(null);
    const [error, setError] = useState<boolean>(false);
    const [borderWidth, setBorderWidth] = useState("1px");
    const [backgroundColor, setBackgroundColor] = useState("#F1F1F1");

    const fetchIsRequired = async () => {
      try {
        if (isRequired) setBorderWidth("2px");
        else setBorderWidth("1px");
      } catch (error: any) {}
    };

    const fetchIsEditable = async () => {
      try {
        if (isEditable) setBackgroundColor("white");
        else setBackgroundColor("#F1F1F1");
      } catch (error: any) {}
    };

    useEffect(() => {
      if (defaultValue) {
        const startStr = defaultValue.split(",")[0].trim();
        const endStr = defaultValue.split(",")[1].trim();
        setValue([dayjs(startStr || newDate), dayjs(endStr || aftetDate)]);
      }
    }, []);

    useEffect(() => {
      fetchIsRequired();
    }, [isRequired]);

    useEffect(() => {
      fetchIsEditable();
    }, [isEditable]);

    useEffect(() => {
      if (onValueChanged) {
        onValueChanged(value[0].format("YYYY"), value[1].format("YYYY"));
      }
      runOnSearch();
    }, [value]);

    const { runOnSearch } = onSearchStore();

    const handleClose = () => {
      setIsPopperOpen(false);
      secondRef.current?.focus();
    };

    const returnData = (startDate: Dayjs | null, endDate: Dayjs | null) => {
      let start: string = "";
      let end: string = "";
      if (startDate !== null) start = startDate.format("YYYY");
      if (endDate !== null) end = endDate.format("YYYY");
      return { start, end };
    };

    const handleStartDateChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) return;
      setValue([newValue, value[1]]);
      const date = returnData(newValue, value[1]);
      if (onValueChanged) {
        onValueChanged(
          date.start.replace(/-/g, ""),
          date.end.replace(/-/g, "")
        );
      }
    };

    const handleEndDateChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) return;
      setValue([value[0], newValue]);
      const date = returnData(value[0], newValue);
      if (onValueChanged) {
        onValueChanged(
          date.start.replace(/-/g, ""),
          date.end.replace(/-/g, "")
        );
      }
      handleClose();
    };

    const onClick = (id: string) => {
      const diff = id === "before" ? -1 : 1;

      setValue(([start, end]) => [
        start.add(diff, "year"),
        end.add(diff, "year"),
      ]);
    };

    const handleChange = (newValue: Dayjs | null) => {
      if (!newValue || !newValue.isValid()) {
        setError(false);
        onValueChanged?.("", "");
        return;
      }

      const nextYearValue = newValue.add(1, "year");

      setValue([newValue, nextYearValue]);

      if (onValueChanged) {
        if (newValue) {
          const date = newValue.format("YYYY");
          if (date !== "Invalid Date") {
            onValueChanged(date, nextYearValue.format("YYYY"));
          } else {
            onValueChanged("", "");
          }
        } else {
          onValueChanged("", "");
        }
      }
    };

    const handleError = (reason: any) => {
      // 일부만 입력된 경우를 에러로 판단
      if (value && reason === "invalidDate") {
        setError(true);
      } else {
        setError(false);
      }
    };

    const handleAccept = () => {
      setTimeout(() => {
        onFocus && onFocus("current");
      }, 10);
    };

    const handleKeyDown = async (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        await (e.target as HTMLInputElement).blur();
        if (isSearchPanel) {
          e.preventDefault();
          onFocus && onFocus("current");
        } else {
          e.preventDefault();
          onFocus && onFocus("next");
        }
      } else if (e.key === "Tab") {
        await (e.target as HTMLInputElement).blur();
        e.preventDefault();
        onFocus && onFocus("next");
      }
    };

    return (
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="ko">
        <div
          ref={divRef}
          style={{ display: "flex", alignItems: "center", gap: "15px" }}
        >
          <div style={{ width: "100%" }}>
            <DatePicker
              value={value[0]}
              views={["year"]}
              format={"YYYY"}
              onChange={handleChange}
              onAccept={handleAccept}
              onError={handleError}
              disabled={isReadOnly}
              slotProps={{
                textField: {
                  inputRef: inputRef,
                  fullWidth: true,
                  sx: {
                    "& .MuiInputBase-root": {
                      height: StyleSettings.control_height,
                      fontSize: StyleSettings.font,
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderWidth: borderWidth, // 테두리 두께 설정
                      },
                      "&:hover fieldset": {
                        borderWidth: borderWidth, // 호버 시 두께 유지
                      },
                      "&.Mui-focused fieldset": {
                        borderWidth: borderWidth, // 포커스 시 두께 유지
                      },
                    },
                    backgroundColor: backgroundColor,
                  },
                  onKeyDown: handleKeyDown,
                  inputProps: {
                    readOnly: true,
                  },
                },
              }}
            />
          </div>
          <div>
            <SnLabel text="차기년도" type="SearchPanel" />
          </div>
          <div style={{ width: "100%" }}>
            <DatePicker
              value={value[1]}
              format={"YYYY"}
              onChange={handleEndDateChange}
              readOnly={true}
              slotProps={{
                textField: {
                  fullWidth: true,
                  inputRef: secondRef,
                  sx: {
                    "& .MuiInputBase-root": {
                      height: StyleSettings.control_height,
                      fontSize: StyleSettings.font,
                    },
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderWidth: borderWidth, // 테두리 두께 설정
                      },
                      "&:hover fieldset": {
                        borderWidth: borderWidth, // 호버 시 두께 유지
                      },
                      "&.Mui-focused fieldset": {
                        borderWidth: borderWidth, // 포커스 시 두께 유지
                      },
                    },
                    backgroundColor: backgroundColor,
                  },
                  InputProps: {
                    endAdornment: null, // 달력 아이콘 제거
                  },
                },
              }}
            />
          </div>
        </div>
      </LocalizationProvider>
    );
  }
);

export default SnYearPeriodBox;
